import {AfterViewInit, Component, DestroyRef, inject, OnInit, signal, TemplateRef, ViewChild} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TableModule} from 'primeng/table';
import {ButtonModule} from 'primeng/button';
import {CheckboxModule} from 'primeng/checkbox';
import {DatePickerModule} from 'primeng/datepicker';
import {DialogModule} from 'primeng/dialog';
import {InputTextModule} from 'primeng/inputtext';
import {SelectModule} from 'primeng/select';
import {TagModule} from 'primeng/tag';
import {PaginatorModule} from 'primeng/paginator';
import {CardModule} from 'primeng/card';
import {TooltipModule} from 'primeng/tooltip';
import {Menu} from 'primeng/menu';
import {MenuItem} from 'primeng/api';

import {catchError, tap} from 'rxjs/operators';
import {of} from 'rxjs';

import {Entry} from '@app/pages/electronic-field-map/pages/fields/field.service';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {TableColumn} from '@components/data-table/data-table.component';
import {
  ListTableFiltersWrapperParent
} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import {buildFilterQuery} from '@app/utils/_msf';
import {
  buildFieldListPlanFactFilterField,
  buildFieldListSeasonScopeParam,
  createDefaultFieldListPlanFactFilter,
  FieldListPlanFactFilterModel,
  stripFieldListPlanFactKeys,
  buildDefaultParamsWithSeasonScope,
} from '@app/pages/electronic-field-map/pages/fields/field-llst/field-list-plan-fact-filter.util';
import {FieldListSeasonRowsPipe} from '@app/pages/electronic-field-map/pages/fields/field-llst/field-list-season-rows.pipe';
import {FilterOperator} from '@app/utils/filter-operator';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {
  ModalFormAddField
} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-form-add-field/modal-form-add-field';

import {Field, FieldBaseGetQuery, FieldExportRequest, FieldService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {
  FieldFilter,
  FirmService,
  Region,
  VarietyService,
  SoilTypeService,
  AgroclimaticZoneService,
  PersonService,
  CadastreService
} from '@6grain/masofa-monolith-api-client/dictionaries';
import {Crop as ApiCrop} from '@6grain/masofa-monolith-api-client/dictionaries/model/crop';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';

import {FieldExportType} from '@models/export-file-type';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {MsfNumber} from '@app/utils/pipes/format-number';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {RegionDisplay} from '@components/region-display/region-display';
import {StatusTag} from '@app/components/ui/status-tag/status-tag';
import { ListTableFiltersWrapper } from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper';
import { RequestParams } from '@components/select/resource-component';

type FieldListQuery = FieldBaseGetQuery & {seasonScope?: string[]};

@Component({
  selector: 'app-field-management',
  standalone: true,
  templateUrl: './field-list.html',
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TagModule,
    PaginatorModule,
    FormsModule,
    CheckboxModule,
    CardModule,
    DatePickerModule,
    SelectModule,
    DialogModule,
    TooltipModule,
    Menu,
    MsfNumber,
    DateFormatLangPipe,
    RegionDisplay,
    StatusTag,
    FieldListSeasonRowsPipe,
    ListTableFiltersWrapper
  ],
  providers: [DatePipe, DateFormatLangPipe, DialogService],
  styleUrls: ['./field-list.scss']
})
export class FieldList extends ListTableFiltersWrapperParent<Field, FieldService> implements OnInit, AfterViewInit {
  // Dependency injection using inject()
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  public readonly service = inject(FieldService);
  public readonly regionService = inject(RegionService);
  public readonly cropService = inject(CropService);
  public readonly firmService = inject(FirmService);
  protected readonly translate = inject(TranslateService);
  public readonly varietyService = inject(VarietyService);
  public readonly datePipe = inject(DatePipe);
  public readonly dialogService = inject(DialogService);
  public readonly soilTypeService = inject(SoilTypeService);
  public readonly agroClimaticService = inject(AgroclimaticZoneService);
  public readonly personService = inject(PersonService);
  public readonly cadastreService = inject(CadastreService);
  public readonly regionManagement = inject(RegionManagerService);

  // Required abstract members
  override getAllMethod = 'cropMonitoringFieldCustomGetByQueryPost';
  override getTotalMethod = 'cropMonitoringFieldCustomGetTotalCountByQueryPost';
  override filtersForms: IMsfFormBuilder[] = [];
  override storageKey = 'electronic_field_list_table';

  // View templates
  @ViewChild('colorTemplate', {static: true}) colorTemplate!: TemplateRef<unknown>;
  @ViewChild('actionsTemplate', {static: true}) actionsTemplate!: TemplateRef<unknown>;
  @ViewChild('regionTemplate', {static: true}) regionTemplate!: TemplateRef<unknown>;
  @ViewChild('areaTemplate', {static: true}) areaTemplate!: TemplateRef<unknown>;
  @ViewChild('nameTemplate', {static: true}) nameTemplate!: TemplateRef<unknown>;
  @ViewChild('fieldSeasonsShortTemplate', {static: true}) fieldSeasonsShortTemplate!: TemplateRef<unknown>;

  // Signals for reactive state
  readonly totalRecords = signal<number>(0);
  readonly tableColumns = signal<TableColumn[]>([]);
  readonly exportMenuItems = signal<MenuItem[]>([]);
  readonly fieldDetail = signal<Entry | undefined>(undefined);

  // Legacy state (kept for compatibility with parent class)
  fields: Entry[] = [];
  regions: Region[] = [];
  crops: ApiCrop[] = [];
  first = 0;
  pageSize = 10;
  separator = ', ';
  filters: FieldFilter = {};
  override filterModel: Record<string, Object> = createDefaultFieldListPlanFactFilter();
  defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    filters: [],
  };
  public isExporting = false;

  constructor() {
    super();
    this.initExportMenu();
    this.initFiltersForm();
  }

  override ngOnInit(): void {
    this.ensurePlanFactSelectionDefault();
    this.defaultParams = buildDefaultParamsWithSeasonScope(
      this.filterModel as FieldListPlanFactFilterModel,
      this.defaultParams,
    );
    super.ngOnInit();
    this.loadTotalRecords();
    this.initStartAndEndDate();
  }

  ngAfterViewInit(): void {
    this.initTableColumns();
  }

  // ---------------------------
  // Initialization helpers
  // ---------------------------
  protected initFiltersForm(): void {
    this.filtersForms = [
      {
        name: 'name',
        title: 'Field name',
        type: 'input',
        placeholder: 'Enter field name'
      },
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagement,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',

          }
        },
        placeholder: 'Select region',
      },
      {
        name: 'fieldArea',
        title: 'Field area',
        type: 'input-double',
        placeholder: 'Enter field area'
      },
      {
        name: 'CropId',
        title: 'Culture',
        type: 'select-paginate',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost'
      },
      {
        name: 'VarietyId',
        title: 'Variety',
        type: 'select-paginate',
        placeholder: 'Select variety',
        selectService: this.varietyService,
        selectServiceMethod: 'dictionariesVarietyGetByQueryPost',
        dependsOn: 'CropId',
        dependsOnFilterKey: 'CropId',
        conditionalSelectService: [
          {
            when: parentValue => { return true},
            service: this.varietyService,
            method: 'dictionariesVarietyGetByQueryPost',
          }
        ]
      },
      {
        name: 'PlantingDate',
        title: 'Sowing period',
        placeholder: 'Select sowing period',
        type: 'date',
        options: {date: {selectionMode: 'range'}}
      },
      {
        name: 'HarvestingDate',
        title: 'Harvesting period',
        type: 'date',
        options: {date: {selectionMode: 'range'}}
      },
      {
        type: 'select-paginate',
        name: 'agricultureProducerId',
        title: 'Agricultural producer',
        placeholder: 'Select agricultural producer',
        selectServiceMethod: 'dictionariesFirmGetByInnOrNamePost',
        selectService: this.firmService,
        options: {
          select: {
            optionLabel: 'shortName',
            searchBy: 'InnOrName',
            translateBy: 'none',
            optionSubtitle: 'inn',
          }
        }
      },
      buildFieldListPlanFactFilterField(),
    ];
  }

  protected ensurePlanFactSelectionDefault(): void {
    if (!Array.isArray(this.filterModel['planFactSelection'])) {
      this.filterModel = {
        ...this.filterModel,
        ...createDefaultFieldListPlanFactFilter(),
      };
    }
  }

  private initTableColumns(): void {
    this.tableColumns.set([
      {
        field: '',
        header: 'Name',
        sortable: false,
        customTemplate: this.nameTemplate,
        type: 'custom'
      },
      {
        field: '',
        header: 'Region',
        type: 'custom',
        customTemplate: this.regionTemplate,
        sortable: false
      },
      {
        field: 'fieldArea',
        header: 'Area of field, ha',
        width: '150px',
        type: 'custom',
        customTemplate: this.areaTemplate
      },
      {
        field: 'lastBidDate',
        header: 'Date of examination',
        type: 'date',
        sortable: false
      },
      {
        field: '',
        header: 'Cultures',
        sortable: false,
        minWidth: '280px',
        type: 'custom',
        customTemplate: this.fieldSeasonsShortTemplate
      },
      {
        field: 'agricultureProducerName',
        header: 'Agricultural producer',
        sortable: false
      },
      {
        field: '',
        header: '',
        type: 'custom',
        customTemplate: this.actionsTemplate
      }
    ]);
  }

  private initExportMenu(): void {
    this.exportMenuItems.set([
      {
        label: this.translate.instant('GeoJson'),
        command: () => this.exportFields(FieldExportType.GeoJson)
      },
      {
        label: this.translate.instant('KML'),
        command: () => this.exportFields(FieldExportType.KML)
      },
      {
        label: this.translate.instant('ShapeFile'),
        command: () => this.exportFields(FieldExportType.ShapeFile)
      },
      {
        label: this.translate.instant('Csv'),
        command: () => this.exportFields(FieldExportType.Csv)
      }
    ]);
  }

  // ---------------------------
  // Data loading
  // ---------------------------
  private loadTotalRecords(filters: Object[] = []): void {
    this.service
      .cropMonitoringFieldCustomGetTotalCountByQueryPost(this.buildFieldsQuery(filters))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(0)),
        tap(total => this.totalRecords.set(total as number))
      )
      .subscribe();
  }

  // ---------------------------
  // Export helpers
  // ---------------------------
  exportFields(format: FieldExportType = FieldExportType.KML): void {
    this.isExporting = true;
    const filters = this.filtersParams;

    const exportCall$ = format === FieldExportType.Csv
      ? this.exportAsCSV(filters)
      : this.exportByFormat(filters, format);

    exportCall$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.handleExportResponse(response, format);
          this.isExporting = false;
        },
        error: (err) => {
          console.error('Export failed:', err);
          this.isExporting = false;
        }
      });
  }

  private buildFieldsQuery(filters: Object[]): FieldListQuery {
    const seasonScope = buildFieldListSeasonScopeParam(this.filterModel as FieldListPlanFactFilterModel);

    return {
      filters: (filters || []) as FieldFilter[],
      ...(seasonScope ? {seasonScope} : {}),
    };
  }

  private exportAsCSV(filters: Object[]) {
    const params = this.buildFieldsQuery(filters);

    return this.service.cropMonitoringFieldExportFromCSVPost(
      params as FieldBaseGetQuery,
      'body',
      false,
      {httpHeaderAccept: 'application/octet-stream'} as any,
    );
  }

  private exportByFormat(filters: Object[], format: FieldExportType) {
    const params = this.buildExportParams(filters, format);

    return this.service.cropMonitoringFieldExportFieldsPost(
      params,
      'body',
      false,
      {httpHeaderAccept: 'application/octet-stream'} as any,
    );
  }

  private buildExportParams(filters: Object[], format: FieldExportType): FieldExportRequest {
    return {
      fieldsQuery: this.buildFieldsQuery(filters) as FieldBaseGetQuery,
      fieldExportType: format,
    };
  }

  private handleExportResponse(response: unknown, format: FieldExportType): void {
    if (response instanceof Blob) {
      this.downloadFile(response, format);
      return;
    }

    const asAny = response as { body?: Blob };
    if (asAny?.body instanceof Blob) {
      this.downloadFile(asAny.body, format);
    }
  }

  private downloadFile(blob: Blob, format: FieldExportType): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fields-export${this.getExtensionForFormat(format)}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private getExtensionForFormat(format: FieldExportType): string {
    const extensions: Record<FieldExportType, string> = {
      [FieldExportType.GeoJson]: '.zip',
      [FieldExportType.KML]: '.zip',
      [FieldExportType.Csv]: '.csv',
      [FieldExportType.ShapeFile]: '.zip'
    };
    return extensions[format] || '';
  }

  // ---------------------------
  // UI interactions
  // ---------------------------
  openEditModal(row: { field: { id: string } } | Entry): void {
    this.fieldDetail.set(row as Entry);
    const id = (row as { field: { id: string } })?.field?.id;

    if (id) {
      this.router.navigate([`/electronic-field-map/fields/${id}/general`]);
    }
  }

  openAddModal(): void {
    const ref: DynamicDialogRef = this.dialogService.open(ModalFormAddField, {
      header: this.translate.instant('Add') + ' ' + this.translate.instant('Field'),
      width: '80%',
      closable: true,
      modal: true,
      data: {
        service: this.service,
        regionService: this.regionService,
        soilTypeService: this.soilTypeService,
        agroClimaticService: this.agroClimaticService,
        firmService: this.firmService,
        personService: this.personService
      }
    });

    ref.onClose.subscribe((result) => {
      if (result?.success) {
        this.loadTotalRecords();
        // Refresh the table if needed
        if (this.applyFilters) {
          this.applyFilters();
        }
      }
    });
  }

  override async applyFilters(): Promise<void> {
    // Clone so nested planFactSelection array changes are not missed.
    this.filterModel = {
      ...this.filterModel,
      planFactSelection: Array.isArray(this.filterModel['planFactSelection'])
        ? [...(this.filterModel['planFactSelection'] as string[])]
        : this.filterModel['planFactSelection'],
    };

    const filters = stripFieldListPlanFactKeys({...this.filterModel} as Record<string, unknown>);
    let paramsFilter = buildFilterQuery(filters as Record<string, Object>) as FieldFilter[];

    paramsFilter = this.processFilters(paramsFilter);
    paramsFilter = await this.regionManagement.includeChildInRequest(
      this.filterModel as Record<string, unknown>,
      [...paramsFilter],
    ) as FieldFilter[];

    this.defaultParams = buildDefaultParamsWithSeasonScope(
      this.filterModel as FieldListPlanFactFilterModel,
      this.defaultParams,
    );

    this.filtersParams = paramsFilter;
    this.loadTotalRecords(this.filtersParams);
    this.initStartAndEndDate();
    // Trigger table reload after defaultParams/filtersParams are set (ResourceComponent.toggleFilter).
    this.toggleFilter = !this.toggleFilter;
  }

  override clearFilters(): void {
    this.filterModel = createDefaultFieldListPlanFactFilter();
    this.defaultParams = buildDefaultParamsWithSeasonScope(
      this.filterModel as FieldListPlanFactFilterModel,
      this.defaultParams,
    );
    this.filtersParams = [];
    this.toggleFilter = !this.toggleFilter;
    this.loadTotalRecords(this.filtersParams);
  }

  private processFilters(filters: FieldFilter[]): FieldFilter[] {
    const processed: FieldFilter[] = [];

    filters.forEach((filter) => {
      // Apply Contains operator for name filter
      if (filter.filterField === 'name' && filter.filterValue) {
        filter.filterOperator = FilterOperator.Contains;
        processed.push(filter);
        return;
      }

      // Split date range filters
      if (this.isDateRangeFilter(filter)) {
        const dateFilters = this.splitDateRangeFilter(filter);
        processed.push(...dateFilters);
      } else {
        processed.push(filter);
      }
    });

    return processed;
  }

  private isDateRangeFilter(filter: FieldFilter): boolean {
    return (
      (filter.filterField === 'HarvestingDate' || filter.filterField === 'PlantingDate') &&
      Array.isArray(filter.filterValue)
    );
  }

  private splitDateRangeFilter(filter: FieldFilter): FieldFilter[] {
    const [startDate, endDate] = filter.filterValue as [Date | null, Date | null];
    const filters: FieldFilter[] = [];

    if (startDate) {
      filters.push({
        filterField: filter.filterField,
        filterOperator: FilterOperator.GreaterThanOrEqual,
        filterValue: this.datePipe.transform(startDate, 'MM.dd.yyyy') as string
      });
    }

    if (endDate) {
      filters.push({
        filterField: filter.filterField,
        filterOperator: FilterOperator.LessThanOrEqual,
        filterValue: this.datePipe.transform(endDate, 'MM.dd.yyyy') as string
      });
    }

    return filters;
  }

  initStartAndEndDate(): void {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return;

    try {
      const params = JSON.parse(raw);
      if (!params?.filters) return;

      params.filters.forEach((filter: FieldFilter) => {
        this.applyStoredDateFilter(filter);
      });
    } catch (error) {
      console.warn('Failed to parse stored filters:', error);
    }
  }

  private applyStoredDateFilter(filter: FieldFilter): void {
    const val = filter.filterValue;
    const key = `${filter.filterField}:${filter.filterOperator}`;

    const dateFilterMap: Record<string, string> = {
      [`PlantingDate:${FilterOperator.GreaterThanOrEqual}`]: 'PlantingDateStart',
      [`PlantingDate:${FilterOperator.LessThanOrEqual}`]: 'PlantingDateEnd',
      [`HarvestingDate:${FilterOperator.GreaterThanOrEqual}`]: 'HarvestingDateStart',
      [`HarvestingDate:${FilterOperator.LessThanOrEqual}`]: 'HarvestingDateEnd'
    };

    const modelKey = dateFilterMap[key];
    if (modelKey) {
      this.filterModel[modelKey] = new Date(val);
    }
  }
}
